import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
}                           from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ExpanderComponent }                                         from '../../../../table/cell-components/actions/expander.component';
import { HaynesProDiagnosticToolVesaFaultCodesExpandedRowComponent } from './haynes-pro.diagnostic-tool.vesa-fault-codes.expanded-row.component';
import {
  HaynesProVesaComponentModel,
  HaynesProVesaDiagnosticConnectorModel,
  HaynesProVesaFaultCodeComponentModel,
  HaynesProVesaFaultCodeModel
}                                                                    from '../typings/haynes-pro.vesa';
import { MessagesService }                                           from '../../../../messages/messages.service';
import { Table }                                                     from '../../../../table/types/table';
import { TableQueryEvent }                                           from '../../../../table/types/table-query-event';
import { VesaService }                                               from '../vesa.service';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-vesa-fault-codes',
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-fault-codes.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-fault-codes.component.html')
})
export class HaynesProDiagnosticToolVesaFaultCodesComponent implements OnDestroy, OnInit {

  @Input() connectorImage : HaynesProVesaDiagnosticConnectorModel;
  @Input() faultCodes     : Array<HaynesProVesaFaultCodeModel>;
  @Input() selections     : Array<{
    description              ?: string;
    group_text               ?: string;
    system_type_id           ?: string;
    wiring_diagram_system_id ?: string;
  }> = [];
  @Input() vehicleId      : number;

  @Output() componentSelect = new EventEmitter<{ component : HaynesProVesaComponentModel; systemTypeId : string }>();

  private isAlive                                = true;
  private readonly broadcastName : string        = 'worVesaFaultCodesTableRefresh';
  private readonly pageSize      : number        = 50;
  private readonly pageSizes     : Array<number> = [ 5, 10, 20, 50, 100 ];

  displayedCodes : Array<HaynesProVesaFaultCodeModel> = [];
  filteredCodes  : Array<HaynesProVesaFaultCodeModel> = [];
  searchText                                          = '';
  tableSettings  : Table;

  constructor (
    private cdr             : ChangeDetectorRef,
    private messagesService : MessagesService,
    public translateService : TranslateService,
    private vesaService     : VesaService
  ) {}

  ngOnDestroy () : void {
    this.isAlive = false;
    delete window[this.broadcastName];
  }

  ngOnInit () : void {
    this.filteredCodes  = this.faultCodes || [];
    this.displayedCodes = this.filteredCodes.slice(0, this.pageSize + 1);
    this.buildTableSettings();
  }

  handleRowExpansion = ( row : HaynesProVesaFaultCodeModel ) : void => {
    if (row.__activated) {
      return;
    }

    this.vesaService.getFilteredFaultCodesWithDiagnosticSteps(this.vehicleId, row.fault_code)
    .then(( data : any ) => {
      row.__diagnosticData = data || {};
      row.__activated      = true;
    })
    .catch(( err : any ) => this.messagesService.error(err))
    .finally(() => { if (this.isAlive) { this.cdr.detectChanges(); } });
  };

  handleShowDiagnosis = ( component : HaynesProVesaFaultCodeComponentModel ) : void => {
    if (!component?.symbol_instance_id || !component?.system_type_id) {
      return;
    }

    this.componentSelect.emit({
      component: {
        id  : component.symbol_instance_id,
        name: component.name,
        type: ''
      },
      systemTypeId: component.system_type_id
    });
  };

  handleTableQuery = ( event : TableQueryEvent ) : void => {
    const offset = event.params.offset || 0;
    const size   = event.params.pageSize || this.pageSize;

    this.displayedCodes = this.filteredCodes.slice(offset, offset + size);
  };

  onSearch () : void {
    const term = (this.searchText || '').toLowerCase().trim();

    if (!term) {
      this.filteredCodes = this.faultCodes || [];
    } else {
      const terms = term.split(',').map(t => t.trim()).filter(Boolean);

      this.filteredCodes = (this.faultCodes || []).filter(code => {
        const codeStr = (code.fault_code || '').toLowerCase();
        const textStr = (code.fault_code_text || '').toLowerCase();

        return terms.some(t => codeStr.includes(t) || textStr.includes(t));
      });
    }

    this.displayedCodes = this.filteredCodes.slice(0, this.pageSize + 1);

    if (typeof window[this.broadcastName] === 'function') {
      window[this.broadcastName]();
    }
  }

  onSearchKeydown ( event : KeyboardEvent ) : void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  private buildTableSettings () : void {
    this.tableSettings = {
      broadcast: this.broadcastName,
      columns  : [
        {
          field : 'fault_code',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.FAULT_CODE')
          }
        },
        {
          classes: 'fault-code-description-col',
          field  : 'fault_code_text',
          header : {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                isExpandable: true
              }
            },
            enabled: true,
            factory: ExpanderComponent
          },
          sortable: false
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_RECORDS_FOUND', {
          type: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.FAULT_CODES')
        })
      },
      rows: {
        expand: {
          enabled     : true,
          factory     : HaynesProDiagnosticToolVesaFaultCodesExpandedRowComponent,
          isExpandable: true,
          bindings    : {
            inputs: {
              onShowDiagnosis  : this.handleShowDiagnosis,
              rowExpansionData : this.handleRowExpansion,
              selectedSystems  : this.selections || [],
              vehicleId        : this.vehicleId
            }
          }
        },
        highlight: {
          enabled: true
        }
      },
      pagination: {
        enabled   : true,
        pageSize  : this.pageSize,
        pageSizes : this.pageSizes,
        queryCount: () => Promise.resolve(this.filteredCodes.length)
      },
      sorting: {
        enabled: false
      }
    };
  }
}
